import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Wallet,
  ArrowDownCircle,
  ArrowUpCircle,
  Users,
  TrendingUp,
  RefreshCw,
  DollarSign,
} from "lucide-react";
import api from "../utils/axios";
import { useAuth } from "../context/AuthContext";
import { formatCurrency, formatNumber, formatAmount } from "../helpers";
/* ================= TYPES ================= */

type GroupedData = {
  counts?: Record<string, number>;
  list?: Record<string, any[]> | any[];
  all?: any[];
};

type UserProfile = {
  uid: number | string;
  name: string;
  email: string;
  created_at: string;
  balance: number;

  total_referrals: number;
  today_referrals: number;
  total_commission: number;

  total_deposit: number;
  today_deposit: number;
  total_withdrawal: number;
  today_withdrawal: number;

  total_win_bet_amount: number;
  today_win_bet_amount: number;
  total_lose_bet_amount: number;
  today_lose_bet_amount: number;
};

const tabs = [
  { key: "members", label: "Members", icon: Users },
  { key: "deposit", label: "Deposits", icon: ArrowUpCircle },
  { key: "withdrawal", label: "Withdrawals", icon: ArrowDownCircle },
  { key: "bets", label: "Bets", icon: Wallet },
];

const flattenRecords = (data: any): any[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.all)) return data.all;
  if (Array.isArray(data.list)) return data.list;
  if (data.list && typeof data.list === "object") {
    return Object.values(data.list).flat();
  }
  return [];
};

/* ================= COMPONENT ================= */

const CollaboratorsProfile: React.FC = () => {
  const params = useParams<{ id?: string; uid?: string }>();
  const { user: authUser } = useAuth();

  const [user, setUser] = useState<UserProfile | null>(null);

  const [members, setMembers] = useState<GroupedData | null>(null);
  const [deposits, setDeposits] = useState<GroupedData | null>(null);
  const [withdrawals, setWithdrawals] = useState<GroupedData | null>(null);
  const [betHistory, setBetHistory] = useState<GroupedData | null>(null);

  const [activeTab, setActiveTab] = useState("members");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(true);

  /* ================= FETCH ================= */

  const fetchUser = async () => {
    try {
      setLoading(true);
      const targetId = params?.id || params?.uid || (authUser as any)?.id;
      if (!targetId) return;
      const res = await api.get(`/user/${targetId}/collaborators/detail`);
      const d = res.data.data;

      setUser({
        uid: d.user.uid,
        name: d.user.name,
        email: d.user.email,
        created_at: d.user.created,
        balance: Number(d.user.balance) || 0,

        total_referrals: d.network_stats?.total_referrals ?? d.members?.counts?.total ?? 0,
        today_referrals: d.network_stats?.today_referrals || 0,

        total_commission:
          (d.user.total_bet_level_commission || 0) +
          (d.user.total_deposit_level_commission || 0) +
          (d.user.total_referral_reward || 0),

        total_deposit: d.network_stats?.total_deposit || 0,
        today_deposit: d.network_stats?.today_deposit || 0,
        total_withdrawal: d.network_stats?.total_withdrawal || 0,
        today_withdrawal: d.network_stats?.today_withdrawal || 0,

        total_win_bet_amount: d.network_stats?.total_win_bet_amount || 0,
        today_win_bet_amount: d.network_stats?.today_win_bet_amount || 0,
        total_lose_bet_amount: d.network_stats?.total_lose_bet_amount || 0,
        today_lose_bet_amount: d.network_stats?.today_lose_bet_amount || 0,
      });

      setMembers(d.members);
      setDeposits(d.deposits);
      setWithdrawals(d.withdrawals);
      setBetHistory(d.bet_history);

      setRows(flattenRecords(d.members));
      setTabLoading(false);
    } catch (err) {
      console.error("User fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= TAB SWITCHING ================= */

  const getActiveData = (): GroupedData | null => {
    if (activeTab === "members") return members;
    if (activeTab === "deposit") return deposits;
    if (activeTab === "withdrawal") return withdrawals;
    if (activeTab === "bets") return betHistory;
    return null;
  };

  useEffect(() => {
    const data = getActiveData();
    setTabLoading(true);
    setRows(flattenRecords(data));
    setTabLoading(false);
  }, [activeTab, members, deposits, withdrawals, betHistory]);

  useEffect(() => {
    fetchUser();
  }, []);

  /* ================= LOADING ================= */

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full" />
      </div>
    );
  }

  /* ================= RENDER ================= */

  return (
    <div className="min-h-screen max-w-7xl mx-auto space-y-6">

      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Agent Profile
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage agent details, members, and transactions
          </p>
        </div>
        <button
          onClick={fetchUser}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {/* ================= SUMMARY ================= */}
      <Section title="Overview">
        <StatGrid>
          <StatCard title="Balance" value={formatCurrency(user.balance)} icon={Wallet} />
          <StatCard title="Total Commission" value={formatCurrency(user.total_commission)} icon={DollarSign} />
          <StatCard title="Total Members" value={String(user.total_referrals)} icon={Users} />
          <StatCard title="Today New Members" value={String(user.today_referrals)} icon={TrendingUp} />
        </StatGrid>
      </Section>

      {/* ================= DEPOSITS & WITHDRAWALS ================= */}
      <Section title="Transactions">
        <StatGrid>
          <StatCard title="Total Deposit" value={formatCurrency(user.total_deposit)} icon={ArrowUpCircle} />
          <StatCard title="Today Deposit" value={formatCurrency(user.today_deposit)} icon={ArrowUpCircle} />
          <StatCard title="Total Withdrawal" value={formatCurrency(user.total_withdrawal)} icon={ArrowDownCircle} />
          <StatCard title="Today Withdrawal" value={formatCurrency(user.today_withdrawal)} icon={ArrowDownCircle} />
        </StatGrid>
      </Section>

      {/* ================= BET STATS ================= */}
      <Section title="Bet Statistics">
        <StatGrid>
          <StatCard title="Total Win Bet" value={formatCurrency(user.total_win_bet_amount)} icon={Wallet} />
          <StatCard title="Today Win Bet" value={formatCurrency(user.today_win_bet_amount)} icon={Wallet} />
          <StatCard title="Total Lose Bet" value={formatCurrency(user.total_lose_bet_amount)} icon={Wallet} />
          <StatCard title="Today Lose Bet" value={formatCurrency(user.today_lose_bet_amount)} icon={Wallet} />
        </StatGrid>
      </Section>

      {/* ================= TABS + TABLE ================= */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* TAB BUTTONS */}
        <div className="flex border-b border-gray-200 bg-gray-50/50">
          {tabs.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-5 py-3.5 flex items-center gap-2 text-sm font-medium transition-colors border-b-2 ${
                  isActive
                    ? "border-emerald-600 text-emerald-600 bg-white font-semibold"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                }`}
              >
                <Icon size={16} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* TABLE */}
        <div className="p-4">
          {tabLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-emerald-600" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              No records found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/75 text-xs uppercase font-semibold text-gray-500">
                    {activeTab === "members" ? (
                      <>
                        <th className="px-4 py-3">Name / Phone</th>
                        <th className="px-4 py-3">Email</th>
                        <th className="px-4 py-3">Joined Date</th>
                      </>
                    ) : (
                      <>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Amount</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Type</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                      {activeTab === "members" ? (
                        <>
                          <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                          <td className="px-4 py-3 text-gray-600">{r.email || "-"}</td>
                          <td className="px-4 py-3 text-gray-500">
                            {r.created ? new Date(r.created).toLocaleString() : "-"}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3 text-gray-500">
                            {new Date(
                              r.transaction_date || r.bet_timestamp || r.created
                            ).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 font-bold text-gray-900">
                            ${r.amount || r.bet_amount || 0}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
                                r.status === "accepted" || r.status === "success"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : r.status === "pending"
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {r.status || "-"}
                            </span>
                          </td>
                          <td className="px-4 py-3 capitalize text-gray-600">
                            {r.req_type || activeTab}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ================= UI HELPERS ================= */

const Section = ({ title, children }: any) => (
  <div>
    <h2 className="text-lg font-semibold mb-3">{title}</h2>
    {children}
  </div>
);

const StatGrid = ({ children }: any) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{children}</div>
);

const StatCard = ({
  title,
  value,
  icon: Icon,
}: {
  title: string;
  value: string;
  icon: any;
}) => (
  <div className="bg-white p-4 rounded-lg shadow border">
    <div className="flex items-center gap-2 mb-2">
      <Icon size={18} />
      <span className="text-sm font-medium">{title}</span>
    </div>
    <div className="text-xl font-bold">{value}</div>
  </div>
);

export default CollaboratorsProfile;
